import React from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import Layout from '../../../containers/Layout';
import FormContainer from '../../../components/AddOrEditPage/FormContainer';
import AddOrEditPageTabs from '../../../components/AddOrEditPage/AddOrEditPageTabs';
import Table from '../../../components/Table';
import DownloadDocumentButton from '../../../components/DownloadDocumentButton';
import FinalizeButton from '../../../components/Imports/FinalizeButton';

import { useUiContext } from '../../../context/UiContext';
import ImportService from '../../../services/ImportService';
import useImportForm from '../../../forms/admin/import/import';
import useDocumentForm from '../../../forms/admin/import/document';
import UploadService from '../../../services/UploadService';
import DocumentService from '../../../services/DocumentService';
import ImportDocumentService from '../../../services/ImportDocumentService';
import { IMPORT_DOCUMENT_TYPES } from '../../../constants/formOptions';

export default function ImportPage() {
  const { showToast } = useUiContext();

  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';

  const { data: currentImport, refetch } = useQuery({
    queryKey: ['import', id],
    queryFn: () => ImportService.getImport(id),
    enabled: isEdit,
  });

  const { mutate: createImport, isPending } = useMutation({
    mutationFn: (formValues) => ImportService.createImport(formValues),
    onSuccess: (newImport) => {
      showToast({
        text: 'Import created successfully',
        type: 'success',
      });
      navigate(`/loans/imports/${newImport.id}`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const hasNoDocuments = !currentImport?._import_documents?.length;

  const { mutate: uploadDocument, isPending: isDocumentUploading } =
    useMutation({
      mutationFn: async (formValues) => {
        const uploadedFile = await UploadService.uploadAttachment(
          formValues.doc
        );
        const doc = await DocumentService.createDocument({
          formValues: {
            private_document: uploadedFile,
            status: 'accepted',
            parseur_mailbox_description: hasNoDocuments
              ? 'NOTE'
              : formValues.type,
          },
        });

        return ImportDocumentService.createImportDocument({
          documentId: doc.id,
          importId: id,
        });
      },
      onSuccess: (doc) => {
        showToast({
          text: 'Document added to the import',
          type: 'success',
        });
        navigate(`/loans/imports/${id}/documents/${doc.id}`);
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const tabs = [
    {
      label: 'Loans',
      onClick: () => {
        navigate('/loans');
      },
    },
    {
      label: 'Imports',
      onClick: () => {
        navigate('/loans/imports');
      },
      active: true,
    },
  ];

  const cols = [
    {
      title: 'Date started',
      keys: ['created_at'],
      type: 'date',
    },
    {
      title: 'Description',
      keys: ['_document.parseur_mailbox_description'],
      format: (value) => {
        // eslint-disable-next-line no-shadow
        const map = IMPORT_DOCUMENT_TYPES.reduce((acc, { value, label }) => {
          acc[value] = label;
          return acc;
        }, {});

        return map[value] || value;
      },
    },
    {
      title: 'Status',
      keys: ['status'],
      format: (value) => value.toLowerCase(),
      link: (row) => `/loans/imports/${id}/documents/${row.id}`,
    },
    {
      title: 'Import',
      keys: ['nothere'],
      type: 'date',
      format: () => currentImport?.created_at,
    },
    {
      title: 'Lender',
      keys: ['notpresent'],
      format: () => currentImport?._lender.name,
    },
    {
      title: '',
      type: 'component',
      component: DownloadDocumentButton,
      props: (row) => ({
        doc: row._document || row._photo,
      }),
    },
  ];

  const form = useImportForm(createImport, currentImport);
  const documentForm = useDocumentForm(uploadDocument, hasNoDocuments);

  const isFinalizeButtonVisible =
    currentImport?.status !== 'COMPLETED' && !!currentImport?.loan_id;

  return (
    <Layout>
      <div className="flex min-h-[calc(100dvh-4rem)]">
        {/* Content area */}
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <main className="grow">
            <div className="px-4 sm:px-6 lg:px-8 py-8 pb-10 w-full max-w-9xl mx-auto">
              {/* Page header */}
              <div className="sm:flex sm:justify-between sm:items-center mb-8">
                {/* Left: Title */}
                <div className="mb-4 sm:mb-0 flex justify-between w-full flex-col sm:flex-row gap-2">
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    {isEdit ? 'Edit Import' : 'Create Import'}
                  </h1>
                  {isFinalizeButtonVisible && (
                    <FinalizeButton
                      refetch={refetch}
                      importId={currentImport?.id}
                    />
                  )}
                </div>
              </div>

              <div className="mb-2 sm:w-60 relative z-0">
                <AddOrEditPageTabs tabs={tabs} />
              </div>

              <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
                <div className="mb-10">
                  <FormContainer
                    groupSizeOffset={10}
                    isVisible={false}
                    form={form}
                    noBillComId
                    isSubmitting={isPending}
                    noTitle
                    noSubmit={isEdit}
                  />
                </div>
                {isEdit && (
                  <div className="mb-10">
                    <FormContainer
                      form={documentForm}
                      noBillComId
                      isSubmitting={isDocumentUploading}
                    />
                  </div>
                )}
                {isEdit && (
                  <Table
                    rows={currentImport?._import_documents}
                    cols={cols}
                    title="Documents"
                  />
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
}
